import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import { FiArrowRight, FiCheckCircle, FiShield } from 'react-icons/fi';

const AuditForm = () => {
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    const formData = new FormData(e.target);
    
    try {
      const response = await fetch("https://formspree.io/f/myklzeaw", {
        method: "POST",
        body: formData,
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (response.ok) {
        setSubmitted(true);
      }
    } catch (error) {
      console.error("Form submission error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-6">
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full bg-white p-8 md:p-12 rounded-[2.5rem] shadow-xl shadow-zinc-900/5 text-center border border-zinc-200"
        >
          <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-8">
            <SafeIcon icon={FiCheckCircle} className="w-10 h-10 text-green-500" />
          </div>
          <h2 className="text-3xl font-black text-zinc-950 mb-4 tracking-tight">Got it.</h2>
          <p className="text-zinc-600 text-lg leading-relaxed">
            Your audit is in progress.<br />
            You'll receive your report within 48 hours.
          </p>
          <div className="mt-10">
            <Link to="/" className="text-zinc-400 hover:text-zinc-950 font-medium transition-colors">
              Return to home
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 py-12 px-6">
      <div className="max-w-2xl mx-auto">
        {/* Logo Link */}
        <Link to="/" className="flex items-center gap-2 mb-12 justify-center group">
          <div className="w-8 h-8 bg-zinc-950 rounded flex items-center justify-center transition-transform group-hover:scale-110">
            <div className="w-4 h-4 border-2 border-white rounded-sm rotate-45" />
          </div>
          <span className="font-black text-xl tracking-tighter text-zinc-950">Data Risk Audit</span>
        </Link>

        <div className="bg-white rounded-[2.5rem] border border-zinc-200 shadow-xl shadow-zinc-900/5 overflow-hidden">
          <div className="bg-zinc-950 p-8 md:p-10 text-white relative">
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full blur-3xl -mr-16 -mt-16"></div>
            <h1 className="text-3xl md:text-4xl font-black tracking-tight mb-3 relative z-10">
              Complete Your Data Risk Audit
            </h1>
            <p className="text-zinc-400 font-medium relative z-10">
              Takes less than 1 minute. Your report will be delivered within 48 hours.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="p-8 md:p-12 space-y-10">
            {/* Q1: Name */}
            <div className="space-y-3">
              <label htmlFor="name" className="block text-sm font-bold uppercase tracking-widest text-zinc-500">
                What's your name?
              </label>
              <input 
                required
                type="text" 
                id="name" 
                name="name" 
                placeholder="Enter your name"
                className="w-full px-0 py-4 bg-transparent border-b-2 border-zinc-200 focus:border-zinc-950 outline-none transition-colors text-xl font-medium text-zinc-900 placeholder:text-zinc-300"
              />
            </div>

            {/* Q2: Email */}
            <div className="space-y-3">
              <label htmlFor="email" className="block text-sm font-bold uppercase tracking-widest text-zinc-500">
                Where should we send your report?
              </label>
              <input 
                required
                type="email" 
                id="email" 
                name="email" 
                placeholder="email@example.com"
                className="w-full px-0 py-4 bg-transparent border-b-2 border-zinc-200 focus:border-zinc-950 outline-none transition-colors text-xl font-medium text-zinc-900 placeholder:text-zinc-300"
              />
            </div>

            {/* Q3: Apps */}
            <div className="space-y-3">
              <div className="flex justify-between items-end">
                <label htmlFor="apps" className="block text-sm font-bold uppercase tracking-widest text-zinc-500">
                  List the 5–10 apps you use most daily
                </label>
                <div className="px-2 py-0.5 bg-zinc-100 rounded text-[10px] font-bold text-zinc-500 uppercase tracking-tighter">Essential</div>
              </div>
              <textarea 
                required
                id="apps" 
                name="apps" 
                rows="3"
                placeholder="TikTok, Instagram, Gmail, Uber, etc."
                className="w-full px-4 py-4 bg-zinc-50 rounded-2xl border-2 border-zinc-200 focus:border-zinc-950 focus:bg-white outline-none transition-all text-lg font-medium text-zinc-900 placeholder:text-zinc-300 resize-none"
              ></textarea>
              <p className="text-xs text-zinc-400 font-medium italic">Example: TikTok, Instagram, Gmail, Uber, etc.</p>
            </div>

            {/* Q4: Device */}
            <div className="space-y-4">
              <label className="block text-sm font-bold uppercase tracking-widest text-zinc-500">
                What device do you primarily use?
              </label>
              <div className="grid grid-cols-2 gap-4">
                {['iPhone', 'Android'].map((device) => (
                  <label key={device} className="relative cursor-pointer group">
                    <input type="radio" name="device" value={device} className="peer sr-only" required />
                    <div className="w-full py-4 text-center rounded-xl border-2 border-zinc-100 peer-checked:border-zinc-950 peer-checked:bg-zinc-950 peer-checked:text-white hover:bg-zinc-50 transition-all font-bold text-zinc-600 group-active:scale-95">
                      {device}
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Q5: Concern */}
            <div className="space-y-4">
              <label className="block text-sm font-bold uppercase tracking-widest text-zinc-500">
                What are you most concerned about?
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {['Privacy', 'Financial impact', 'Family / personal safety', 'Just curious'].map((concern) => (
                  <label key={concern} className="relative cursor-pointer group">
                    <input type="radio" name="concern" value={concern} className="peer sr-only" required />
                    <div className="w-full py-3 px-4 rounded-xl border-2 border-zinc-100 peer-checked:border-zinc-950 peer-checked:bg-zinc-950 peer-checked:text-white hover:bg-zinc-50 transition-all font-bold text-zinc-600 text-sm group-active:scale-95">
                      {concern}
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Q6: Profession */}
            <div className="space-y-3">
              <label htmlFor="profession" className="block text-sm font-bold uppercase tracking-widest text-zinc-500">
                What do you do professionally? (optional)
              </label>
              <input 
                type="text" 
                id="profession" 
                name="profession" 
                placeholder="e.g. Business Owner, Software Engineer"
                className="w-full px-0 py-4 bg-transparent border-b-2 border-zinc-200 focus:border-zinc-950 outline-none transition-colors text-lg font-medium text-zinc-900 placeholder:text-zinc-300"
              />
            </div>

            <button 
              disabled={isLoading}
              type="submit" 
              className="w-full py-5 bg-zinc-950 text-white rounded-2xl font-black text-xl hover:bg-zinc-800 transition-all shadow-xl shadow-zinc-900/20 active:scale-[0.98] flex items-center justify-center gap-3 disabled:opacity-50"
            >
              {isLoading ? (
                <div className="w-6 h-6 border-4 border-white/20 border-t-white rounded-full animate-spin"></div>
              ) : (
                <>
                  Submit Audit Request
                  <SafeIcon icon={FiArrowRight} className="w-6 h-6" />
                </>
              )}
            </button>

            <div className="flex items-center justify-center gap-2 text-zinc-400 text-xs font-medium">
              <SafeIcon icon={FiShield} className="w-4 h-4" />
              Your data is encrypted and never sold to third parties.
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AuditForm;